import React from 'react';
import styled from 'styled-components';
import { FaPhone, FaEnvelope, FaLinkedin, FaTwitter, FaGithub } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { useLanguage } from '../i18n';

const FooterContainer = styled.footer`
  background: ${props => props.theme.colors.textDark};
  color: white;
  padding: 80px 0 40px;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const FooterContent = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr;
  gap: 40px;
  margin-bottom: 60px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 32px;
  }
`;

const BrandSection = styled.div`
  .logo {
    display: flex;
    align-items: center;
    font-size: 28px;
    font-weight: 800;
    color: white;
    margin-bottom: 24px;
    
    .logo-icon {
      width: 40px;
      height: 40px;
      background: ${props => props.theme.gradients.primary};
      border-radius: 10px;
      margin-right: 16px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-weight: bold;
    }
  }
  
  .description {
    color: ${props => props.theme.colors.textLight};
    line-height: 1.6;
    margin-bottom: 32px;
  }
  
  .contact-info {
    .contact-item {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-bottom: 16px;
      color: ${props => props.theme.colors.textLight};
      
      .icon {
        width: 20px;
        height: 20px;
        color: ${props => props.theme.colors.primary};
      }
    }
  }
`;

const FooterSection = styled.div`
  h3 {
    font-size: 1.2rem;
    font-weight: 700;
    margin-bottom: 24px;
    color: white;
  }
  
  ul {
    list-style: none;
    
    li {
      margin-bottom: 12px;
      
      a {
        color: ${props => props.theme.colors.textLight};
        transition: color 0.3s ease;
        
        &:hover {
          color: ${props => props.theme.colors.primary};
        }
      }
    }
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 24px;
  
  .social-link {
    width: 40px;
    height: 40px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    transition: all 0.3s ease;
    
    &:hover {
      background: ${props => props.theme.colors.primary};
      transform: translateY(-2px);
    }
  }
`;

const FooterBottom = styled.div`
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 40px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

const Copyright = styled.p`
  color: ${props => props.theme.colors.textLight};
  margin: 0;
`;

const FooterLinks = styled.div`
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    justify-content: center;
  }
  
  a {
    color: ${props => props.theme.colors.textLight};
    transition: color 0.3s ease;
    
    &:hover {
      color: ${props => props.theme.colors.primary};
    }
  }
`;

const Footer: React.FC = () => {
  const { t } = useLanguage();
  const footerSections = [
    {
      title: t('footer.section.eco'),
      links: [
        { name: t('footer.link.resources'), href: "#resources" },
        { name: t('footer.link.ecosystem'), href: "#ecosystem" },
        { name: t('footer.link.partnership'), href: "#partnership" },
        { name: t('footer.link.case'), href: "#cases" }
      ]
    },
    {
      title: t('footer.section.products'),
      links: [
        { name: t('footer.link.suite'), href: "#suite" },
        { name: t('footer.link.market'), href: "#marketplace" },
        { name: t('footer.link.platform'), href: "#platform" },
        { name: t('footer.link.franchise'), href: "#franchise" }
      ]
    },
    {
      title: t('footer.section.industry'),
      links: [
        { name: t('footer.link.edu'), href: "#education" },
        { name: t('footer.link.health'), href: "#healthcare" },
        { name: t('footer.link.ecom'), href: "#ecommerce" },
        { name: t('footer.link.finance'), href: "#finance" }
      ]
    },
    {
      title: t('footer.section.about'),
      links: [
        { name: t('about.vision'), href: "#mission" },
        { name: t('about.team'), href: "#team" },
        { name: t('about.awards'), href: "#awards" },
        { name: t('nav.contact'), href: "#contact" }
      ]
    }
  ];

  return (
    <FooterContainer>
      <Container>
        <FooterContent>
          <BrandSection>
            <div className="logo">
              <div className="logo-icon">M</div>
              {t('footer.brand.name')}
            </div>
            <p className="description">
              {t('footer.brand.desc')}
            </p>
            <div className="contact-info">
              <div className="contact-item">
                <FaPhone className="icon" />
                <span>180-2808-4690</span>
              </div>
              <div className="contact-item">
                <FaEnvelope className="icon" />
                <span>xbmld1@gmail.com</span>
              </div>
            </div>
            <SocialLinks>
              <a href="https://linkedin.com" className="social-link" target="_blank" rel="noopener noreferrer">
                <FaLinkedin />
              </a>
              <a href="https://twitter.com" className="social-link" target="_blank" rel="noopener noreferrer">
                <FaTwitter />
              </a>
              <a href="https://github.com" className="social-link" target="_blank" rel="noopener noreferrer">
                <FaGithub />
              </a>
            </SocialLinks>
          </BrandSection>
          
          {footerSections.map((section, index) => (
            <FooterSection key={index}>
              <h3>{section.title}</h3>
              <ul>
                {section.links.map((link, linkIndex) => (
                  <li key={linkIndex}>
                    <a href={link.href}>{link.name}</a>
                  </li>
                ))}
              </ul>
            </FooterSection>
          ))}
        </FooterContent>
        
        <FooterBottom>
          <Copyright>
            {t('footer.copyright')} 
          </Copyright>
          <FooterLinks>
            <a href="#links">{t('footer.links.friend')}</a>
            <a href="#external-links">{t('footer.links.souwai')}</a>
            <Link to="/privacy">{t('footer.links.privacy')}</Link>
            <Link to="/terms">{t('footer.links.terms')}</Link>
          </FooterLinks>
        </FooterBottom>
      </Container>
    </FooterContainer>
  );
};

export default Footer;
